import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HORECA } from '@/constants/testIds';
import { CheckCircle2, Loader2, Mail, Phone, MapPin } from 'lucide-react';
import LutfiyeLogo from './LutfiyeSignature';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMPANY_TYPES = ['Otel', 'Restoran', 'Cafe', 'Kafeterya', 'Pastane', 'Diğer'];
const PRODUCT_OPTIONS = [
  { slug: 'marmelat', label: 'Marmelat' },
  { slug: 'recel', label: 'Reçel' },
  { slug: 'pekmez', label: 'Pekmez' },
  { slug: 'helva-sanver', label: 'Helva & Şanver' },
  { slug: 'tahin', label: 'Tahin' },
  { slug: 'ceviz-diger', label: 'Ceviz & Diğer' },
];

const initial = {
  company_name: '',
  contact_person: '',
  phone: '',
  email: '',
  company_type: '',
  city: '',
  interested_products: [],
  monthly_need: '',
  message: '',
};

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (label) =>
    setForm((f) => ({
      ...f,
      interested_products: f.interested_products.includes(label)
        ? f.interested_products.filter((x) => x !== label)
        : [...f.interested_products, label],
    }));

  const validate = () => {
    if (!form.company_name.trim()) return 'Firma adı gerekli';
    if (!form.contact_person.trim()) return 'İletişim kişi gerekli';
    if (!/^[+()\d\s-]{6,}$/.test(form.phone.trim())) return 'Geçerli bir telefon girin';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Geçerli bir e-posta girin';
    if (!form.company_type) return 'Firma türü seçin';
    if (!form.city.trim()) return 'Şehir gerekli';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    const err = validate();
    if (err) {
      setErrorMsg(err);
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/horeca/leads`, {
        ...form,
        monthly_need: form.monthly_need || null,
        message: form.message || null,
      });
      setSubmitted(true);
      toast.success('Talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.');
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        'Talebiniz gönderilirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.';
      setErrorMsg(typeof msg === 'string' ? msg : 'Doğrulama hatası');
      toast.error('Gönderim başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="iletisim"
      data-testid={HORECA.contactSection}
      className="relative paper-kraft text-[#1F1F1F] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 relative">
        {/* Sol: bilgi */}
        <div className="lg:col-span-5">
          <span className="eyebrow text-[#B08834] block mb-4">İletişim</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6 font-light">
            Kurumsal teklifiniz için
            <span className="italic text-[#B03A2E]"> birkaç adım</span>.
          </h2>
          <p className="text-[#5A5145] leading-relaxed font-body font-light mb-10 max-w-md">
            Formu doldurun; ekibimiz 1 iş günü içinde iletişime geçsin.
            Numune talebinizi mesaj kısmında belirtebilirsiniz.
          </p>

          <div className="space-y-5 mb-10">
            {[
              { i: Mail, label: 'E-posta', v: 'info@sanverhelva.com' },
              { i: Phone, label: 'Kurumsal Hat', v: '0378 266 11 33' },
              { i: MapPin, label: 'Üretim', v: 'Bartın · Amasra · Ulus' },
            ].map(({ i: Icon, label, v }) => (
              <div key={label} className="flex items-start gap-4">
                <Icon className="w-5 h-5 text-[#B03A2E] mt-1" strokeWidth={1.4} />
                <div>
                  <div className="eyebrow text-[#5A5145] mb-1">{label}</div>
                  <div className="text-[#1F1F1F] font-body">{v}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#1F1F1F]/15 pt-8">
            <LutfiyeLogo
              width="w-32"
              height="h-12"
              color="text-[#1F1F1F]"
            />
            <div className="eyebrow text-[#5A5145] mt-3">
              Amasra kökü — Bugün 81 il
            </div>
          </div>
        </div>

        {/* Sağ: form */}
        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid={HORECA.formSuccess}
              className="bg-[#FFFDF7] text-[#1F1F1F] p-10 md:p-14 text-center gold-frame"
            >
              <CheckCircle2 className="w-14 h-14 text-[#3A5A3F] mx-auto mb-6" strokeWidth={1.2} />
              <h3 className="font-display text-3xl md:text-4xl mb-3 font-light">
                Talebiniz alındı.
              </h3>
              <p className="text-[#5A5145] leading-relaxed max-w-md mx-auto font-body">
                Lutfiye Horeca ekibi 1 iş günü içinde dönecektir.
                Bu arada diğer ürün gruplarımıza göz atabilirsiniz.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-8 text-xs tracking-[0.24em] text-[#B03A2E] hover:text-[#8A2E22] transition-colors font-body"
              >
                YENİ TALEP OLUŞTUR
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid={HORECA.form}
              className="bg-[#FFFDF7] text-[#1F1F1F] p-8 md:p-12 gold-frame"
              noValidate
            >
              {/* Etiket başlığı */}
              <div className="text-center mb-8 pb-6 border-b border-[#1F1F1F]/15">
                <div className="eyebrow text-[#B03A2E] mb-3">Kurumsal Teklif Formu</div>
                <div className="flex justify-center">
                  <LutfiyeLogo
                    width="w-32"
                    height="h-11"
                    color="text-[#1F1F1F]"
                  />
                </div>
                <div className="eyebrow text-[#5A5145] mt-2">Horeca</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Firma Adı" required>
                  <input
                    data-testid={HORECA.fieldCompanyName}
                    type="text"
                    value={form.company_name}
                    onChange={(e) => set('company_name', e.target.value)}
                    placeholder="Örn. Grand Lezzet Otel A.Ş."
                    className="field-underline"
                    required
                  />
                </Field>
                <Field label="İletişim Kişi" required>
                  <input
                    data-testid={HORECA.fieldContactPerson}
                    type="text"
                    value={form.contact_person}
                    onChange={(e) => set('contact_person', e.target.value)}
                    placeholder="Ad Soyad"
                    className="field-underline"
                    required
                  />
                </Field>
                <Field label="Telefon" required>
                  <input
                    data-testid={HORECA.fieldPhone}
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="+90 555 000 00 00"
                    className="field-underline"
                    required
                  />
                </Field>
                <Field label="E-posta" required>
                  <input
                    data-testid={HORECA.fieldEmail}
                    type="email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="isim@firma.com"
                    className="field-underline"
                    required
                  />
                </Field>
                <Field label="Firma Türü" required>
                  <select
                    data-testid={HORECA.fieldCompanyType}
                    value={form.company_type}
                    onChange={(e) => set('company_type', e.target.value)}
                    className="field-underline appearance-none pr-8 bg-[#FFFDF7]"
                    required
                  >
                    <option value="" disabled>Seçiniz</option>
                    {COMPANY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Şehir" required>
                  <input
                    data-testid={HORECA.fieldCity}
                    type="text"
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    placeholder="Örn. İstanbul"
                    className="field-underline"
                    required
                  />
                </Field>
                <Field label="Tahmini Aylık İhtiyaç" className="md:col-span-2">
                  <input
                    data-testid={HORECA.fieldMonthlyNeed}
                    type="text"
                    value={form.monthly_need}
                    onChange={(e) => set('monthly_need', e.target.value)}
                    placeholder="Örn. 200 kavanoz / 50 kg / palet"
                    className="field-underline"
                  />
                </Field>
              </div>

              <div className="mt-10">
                <div className="eyebrow text-[#5A5145] mb-4">
                  İlgilendiğiniz Ürünler
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {PRODUCT_OPTIONS.map((p) => {
                    const active = form.interested_products.includes(p.label);
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        data-testid={HORECA.productCheckbox(p.slug)}
                        onClick={() => toggleProduct(p.label)}
                        className={`text-left px-4 py-3 border transition-all text-sm font-body ${
                          active
                            ? 'bg-[#1F1F1F] text-[#FBF7EE] border-[#1F1F1F]'
                            : 'bg-transparent text-[#1F1F1F] border-[#1F1F1F]/25 hover:border-[#1F1F1F]'
                        }`}
                        aria-pressed={active}
                      >
                        <span className="mr-2">{active ? '✓' : '+'}</span>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-10">
                <div className="eyebrow text-[#5A5145] mb-2">Mesajınız</div>
                <textarea
                  data-testid={HORECA.fieldMessage}
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  placeholder="Numune talebiniz, özel gramaj veya private label ile ilgili notlarınız."
                  rows={4}
                  className="field-underline resize-none"
                />
              </div>

              {errorMsg && (
                <div
                  data-testid={HORECA.formError}
                  className="mt-6 text-sm text-[#B03A2E] border-l-2 border-[#B03A2E] pl-4 py-2 bg-[#B03A2E]/5 font-body"
                >
                  {errorMsg}
                </div>
              )}

              <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-[#5A5145] max-w-md leading-relaxed font-body">
                  Gönder tuşuna basarak KVKK aydınlatma metnini kabul etmiş
                  sayılırsınız.
                </p>
                <button
                  data-testid={HORECA.submitBtn}
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 bg-[#1F1F1F] hover:bg-[#B03A2E] disabled:opacity-60 text-[#FBF7EE] px-8 md:px-10 py-4 text-xs tracking-[0.22em] transition-colors font-body"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      GÖNDERİLİYOR
                    </>
                  ) : (
                    'TEKLİF TALEP ET'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <div className="eyebrow text-[#5A5145] mb-1">
        {label} {required && <span className="text-[#B03A2E]">*</span>}
      </div>
      {children}
    </label>
  );
}
